/**
 * Original explanatory diagrams for the guides.
 *
 * These are inline SVG rather than image files on purpose: they cost no extra
 * HTTP request, stay sharp on any display, are baked into the prerendered HTML
 * so crawlers and non-JS clients see them, and — on a site about vector
 * graphics — they demonstrate the point they are making.
 *
 * Each figure carries a <title>/<desc> for screen readers plus a visible
 * caption, so the explanation survives even if the drawing does not render.
 */

const BRAND = "var(--brand-600)";
const BRAND_SOFT = "var(--brand-200)";
const INK = "var(--text)";
const MUTED = "var(--text-faint)";
const LINE = "var(--border-strong)";
const DANGER = "var(--danger)";

function Figure({ caption, children }) {
  return (
    <figure className="guide-fig">
      {children}
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Real screenshots of the converter, captured from the live site by
 * scripts/capture-walkthrough.mjs. Re-run that script whenever the converter
 * UI changes, or these quietly start describing a tool that no longer exists.
 *
 * Intrinsic dimensions are declared so the browser reserves the space before
 * the image loads — without them three lazy images shift the whole article as
 * they arrive.
 */
const WALKTHROUGH_STEPS = [
  {
    file: "step-1-upload",
    w: 1056,
    h: 722,
    title: "Choose your file",
    body: "Drag the .eps file onto the drop zone, or click Browse files to pick it from your computer. Try a sample runs the same flow on a demo file if you just want to see how it behaves.",
    alt: "The EPS Viewer drop zone, with a Browse files button and a Try a sample button.",
  },
  {
    file: "step-2-processing",
    w: 1056,
    h: 498,
    title: "The file is converted",
    body: "The EPS is uploaded and rendered on the server. A progress bar shows the stage it has reached — a few seconds is typical, longer for large or complex artwork.",
    alt: "A progress bar reading 'Processing filename.eps' partway through the conversion.",
  },
  {
    file: "step-3-result",
    w: 1056,
    h: 1336,
    title: "Preview it, then download",
    body: "The rendered artwork appears straight away. The checkerboard behind it means the background is genuinely transparent, not white. Click the preview to enlarge it, then download as PNG, JPG or PDF.",
    alt: "The converted artwork shown on a checkerboard transparency background, with Download PNG, JPG and PDF buttons beneath it.",
  },
];

/**
 * Three-step visual walkthrough of the converter.
 *
 * `intro` lets each guide frame the sequence in its own words, so the shared
 * component does not repeat identical prose across pages.
 */
export function ToolWalkthroughFigure({ intro }) {
  return (
    <figure className="guide-fig guide-walk">
      <ol className="guide-walk__list">
        {WALKTHROUGH_STEPS.map((s, i) => (
          <li className="guide-walk__step" key={s.file}>
            <div className="guide-walk__head">
              <span className="guide-walk__num" aria-hidden="true">{i + 1}</span>
              <h3 className="guide-walk__title">{s.title}</h3>
            </div>
            <p className="guide-walk__body">{s.body}</p>
            <picture>
              <source srcSet={`/images/walkthrough/${s.file}.webp`} type="image/webp" />
              <img
                className="guide-walk__shot"
                src={`/images/walkthrough/${s.file}.png`}
                width={s.w}
                height={s.h}
                loading="lazy"
                decoding="async"
                alt={s.alt}
              />
            </picture>
          </li>
        ))}
      </ol>
      <figcaption>{intro}</figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The same circle stored two ways. The raster half is generated by testing
 * each cell centre against the circle equation, which is exactly what a
 * rasterizer does — so the stair-stepping is real, not drawn by hand.
 */
export function VectorVsRasterFigure() {
  const cx = 500;
  const cy = 150;
  const r = 96;
  const cell = 24;

  const cells = [];
  for (let x = cx - r - cell; x < cx + r + cell; x += cell) {
    for (let y = cy - r - cell; y < cy + r + cell; y += cell) {
      const dx = x + cell / 2 - cx;
      const dy = y + cell / 2 - cy;
      if (Math.sqrt(dx * dx + dy * dy) <= r) {
        cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={cell - 1} height={cell - 1} style={{ fill: BRAND }} />);
      }
    }
  }

  return (
    <Figure caption="The same circle stored two ways. The vector version records a centre and a radius, so it is redrawn perfectly at any size. The raster version records which pixels are filled — enlarge it and you see the grid.">
      <svg viewBox="0 0 680 330" role="img" aria-labelledby="vr-t vr-d" className="guide-fig__svg">
        <title id="vr-t">Vector versus raster representation of a circle</title>
        <desc id="vr-d">
          On the left, a smooth circle described mathematically. On the right, the same
          circle built from square pixels, showing stepped edges.
        </desc>

        <circle cx="180" cy="150" r="96" style={{ fill: BRAND }} />

        <g>{cells}</g>

        <text x="180" y="285" textAnchor="middle" fontSize="19" fontWeight="700" style={{ fill: INK }}>Vector</text>
        <text x="180" y="309" textAnchor="middle" fontSize="14" style={{ fill: MUTED }}>circle: centre + radius</text>

        <text x="500" y="285" textAnchor="middle" fontSize="19" fontWeight="700" style={{ fill: INK }}>Raster</text>
        <text x="500" y="309" textAnchor="middle" fontSize="14" style={{ fill: MUTED }}>a grid of coloured pixels</text>

        <line x1="340" y1="40" x2="340" y2="255" style={{ stroke: LINE }} strokeWidth="1" strokeDasharray="4 5" />
      </svg>
    </Figure>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Why artwork arrives clipped: strokes are centred on the path, so half the
 * stroke width sits outside the geometry the bounding box was measured from.
 */
export function BoundingBoxFigure() {
  return (
    <Figure caption="The bounding box declares the artwork's extents in points. Because a stroke is centred on its path, half of it sits outside the shape — if the box was measured from the geometry alone, that overhang is what gets clipped.">
      <svg viewBox="0 0 680 300" role="img" aria-labelledby="bb-t bb-d" className="guide-fig__svg">
        <title id="bb-t">How an EPS bounding box can clip artwork</title>
        <desc id="bb-d">
          Left: a correct bounding box surrounding the artwork with room for its stroke.
          Right: a box measured too tightly, cutting off the outer half of the stroke.
        </desc>

        <clipPath id="bbClipTight">
          <rect x="405" y="70" width="200" height="120" />
        </clipPath>

        {/* Correct box */}
        <rect x="60" y="55" width="200" height="150" style={{ fill: "none", stroke: BRAND }} strokeWidth="2" strokeDasharray="7 5" />
        <rect x="90" y="85" width="140" height="90" rx="8" style={{ fill: BRAND_SOFT, stroke: BRAND }} strokeWidth="12" />
        <text x="52" y="222" fontSize="13" style={{ fill: MUTED }}>0 0</text>
        <text x="268" y="48" fontSize="13" style={{ fill: MUTED }} textAnchor="end">200 150</text>
        <text x="160" y="255" textAnchor="middle" fontSize="17" fontWeight="700" style={{ fill: INK }}>Correct box</text>
        <text x="160" y="278" textAnchor="middle" fontSize="13" style={{ fill: MUTED }}>stroke fits inside</text>

        {/* Too-tight box */}
        <g clipPath="url(#bbClipTight)">
          <rect x="405" y="70" width="200" height="120" rx="8" style={{ fill: BRAND_SOFT, stroke: BRAND }} strokeWidth="12" />
        </g>
        <rect x="405" y="70" width="200" height="120" style={{ fill: "none", stroke: BRAND }} strokeWidth="2" strokeDasharray="7 5" />
        <rect x="393" y="58" width="224" height="144" style={{ fill: "none", stroke: DANGER }} strokeWidth="1.5" strokeDasharray="3 4" />
        <text x="505" y="45" textAnchor="middle" fontSize="12.5" style={{ fill: DANGER }}>artwork actually extends to here</text>
        <text x="505" y="255" textAnchor="middle" fontSize="17" fontWeight="700" style={{ fill: INK }}>Box too tight</text>
        <text x="505" y="278" textAnchor="middle" fontSize="13" style={{ fill: MUTED }}>outer half of the stroke is clipped</text>
      </svg>
    </Figure>
  );
}

/* ------------------------------------------------------------------ */

/** The structural layout of an EPS file, top to bottom. */
export function EpsAnatomyFigure() {
  const rows = [
    { label: "Binary DOS header", note: "optional · C5 D0 D3 C6 + byte offsets", faint: true },
    { label: "%!PS-Adobe-3.0 EPSF-3.0", note: "magic line — marks it as encapsulated", mono: true },
    { label: "DSC header", note: "%%BoundingBox, %%Creator, %%Title …", mono: true },
    { label: "Prolog", note: "procedure definitions used by the drawing code" },
    { label: "Script", note: "the drawing itself — moveto, lineto, fill" },
    { label: "Trailer", note: "%%EOF", mono: true },
    { label: "Embedded preview", note: "optional · TIFF or WMF thumbnail, usually 72 DPI", faint: true },
  ];

  const rowH = 46;
  const gap = 8;
  const height = rows.length * (rowH + gap) + 20;

  return (
    <Figure caption="How an EPS file is laid out. Only the middle sections are required — the binary header and the preview thumbnail are optional, which is why some EPS files open cleanly in a text editor and others look like binary garbage.">
      <svg viewBox={`0 0 680 ${height}`} role="img" aria-labelledby="an-t an-d" className="guide-fig__svg">
        <title id="an-t">The structure of an EPS file</title>
        <desc id="an-d">
          Seven stacked sections from top to bottom: optional binary DOS header, the
          PostScript magic line, the DSC header, prolog, script, trailer, and an optional
          embedded preview image.
        </desc>
        {rows.map((r, i) => {
          const y = 10 + i * (rowH + gap);
          return (
            <g key={r.label}>
              <rect
                x="10"
                y={y}
                width="660"
                height={rowH}
                rx="7"
                style={{
                  fill: r.faint ? "var(--bg-softer)" : "var(--brand-50)",
                  stroke: r.faint ? LINE : BRAND_SOFT,
                }}
                strokeWidth="1"
                strokeDasharray={r.faint ? "5 4" : undefined}
              />
              <text
                x="26"
                y={y + 28}
                fontSize="15"
                fontWeight="700"
                style={{ fill: r.faint ? MUTED : INK }}
                fontFamily={r.mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined}
              >
                {r.label}
              </text>
              <text x="654" y={y + 28} fontSize="13" style={{ fill: MUTED }} textAnchor="end">{r.note}</text>
            </g>
          );
        })}
      </svg>
    </Figure>
  );
}

/* ------------------------------------------------------------------ */

const TRANSPARENCY_CAPTION_DEFAULT =
  "JPG has no alpha channel, so a transparent background cannot survive the export — it is filled with a solid colour, almost always white. No quality setting changes this; only PNG (or SVG, or PDF) keeps the transparency.";

/**
 * What happens to a transparent background in each output format.
 *
 * Shared by the PNG, JPG and troubleshooting guides. The default caption leads
 * with JPG's limitation, which is the point being made in the JPG guide but
 * reads as a non-sequitur in the PNG one — so callers can supply their own.
 */
export function TransparencyFigure({ caption = TRANSPARENCY_CAPTION_DEFAULT }) {
  const panels = [
    { x: 20, title: "Source artwork", note: "transparent background", checker: true },
    { x: 245, title: "Exported to PNG", note: "transparency preserved", checker: true },
    { x: 470, title: "Exported to JPG", note: "filled with solid white", checker: false },
  ];

  return (
    <Figure caption={caption}>
      <svg viewBox="0 0 680 250" role="img" aria-labelledby="tr-t tr-d" className="guide-fig__svg">
        <title id="tr-t">Transparency in PNG versus JPG output</title>
        <desc id="tr-d">
          Three panels: source artwork on a transparent background, the same artwork
          exported to PNG with transparency intact, and exported to JPG where the
          background has become solid white.
        </desc>

        <pattern id="tfChecker" width="20" height="20" patternUnits="userSpaceOnUse">
          <rect width="20" height="20" fill="#ffffff" />
          <rect width="10" height="10" fill="#e2e8f0" />
          <rect x="10" y="10" width="10" height="10" fill="#e2e8f0" />
        </pattern>

        {panels.map((p) => (
          <g key={p.title}>
            <rect
              x={p.x}
              y="20"
              width="190"
              height="140"
              rx="8"
              fill={p.checker ? "url(#tfChecker)" : "#ffffff"}
              style={{ stroke: LINE }}
              strokeWidth="1.5"
            />
            <circle cx={p.x + 95} cy="90" r="42" style={{ fill: BRAND }} />
            <path
              d={`M ${p.x + 72} 90 l 16 18 l 30 -34`}
              fill="none"
              stroke="#ffffff"
              strokeWidth="9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text x={p.x + 95} y="197" textAnchor="middle" fontSize="16" fontWeight="700" style={{ fill: INK }}>{p.title}</text>
            <text x={p.x + 95} y="220" textAnchor="middle" fontSize="13" style={{ fill: MUTED }}>{p.note}</text>
          </g>
        ))}
      </svg>
    </Figure>
  );
}
